import { createContext, PropsWithChildren, useContext, useReducer } from 'react';
import { Platform, Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { ShellProviderProps } from '@aviv-estimation-funnel/shells/src/app/factory';
import {
    getFormDefaultValues,
    getMaxDisplayedStepNumber,
    getStepNames,
} from '@mfe-presentation/hooks/useStepUtils';
import { IAction, ICurrentStep, IState } from '@mfe-types/context';
import { IConfiguration } from '@mfe-types/form';
import { TLogo } from '@mfe-types/logo';

import { useTimer } from '../hooks/useTimer';

import { initialState } from './initialState';
import { reducer } from './reducer';

interface IAppContext extends PropsWithChildren {
    config: IConfiguration;
    portal: Portal;
    logo: TLogo;
    useShellContext?: () => ShellProviderProps | undefined;
}

const AppContext = createContext<{ state: IState; dispatch: React.Dispatch<IAction> }>({
    state: initialState,
    dispatch: () => undefined,
});

const useAppContext = () => useContext(AppContext).state;
const useAppDispatch = () => useContext(AppContext).dispatch;

const computeInitialState = ({ config, portal, logo, useShellContext }: IAppContext) => {
    const stepNames = getStepNames(config);
    const formValues = getFormDefaultValues(config);
    const maxStepIndex = stepNames.length - 1;
    const initialStepIndex = initialState.currentStep.index; // we always start the app at the first step
    const defaultCustomSubmit = initialState.currentStep.customSubmit;
    const currentStepName = stepNames[initialStepIndex];
    const displayedStepNumber = Object.values(config)[0]?.displayedStepNumber ?? 0;
    const maxDisplayedStep = getMaxDisplayedStepNumber(config);

    const currentStepInfo: ICurrentStep = {
        name: currentStepName,
        index: initialStepIndex,
        displayedStepNumber,
        customSubmit: defaultCustomSubmit,
    };

    return {
        ...initialState,
        stepNames,
        maxStepIndex,
        maxDisplayedStep,
        formValues: {
            ...formValues,
            // FIX: we need to set these values to false by default
            hasABasement: false,
            hasASwimmingPool: false,
            portal,
            platform: Platform.Website,
        },
        currentStep: currentStepInfo,
        config,
        portal,
        logo,
        ...(useShellContext && { useShellContext }),
    };
};

const AppProvider = ({ config, portal, logo, useShellContext, children }: IAppContext) => {
    const [state, dispatch] = useReducer(
        reducer,
        computeInitialState({ config, portal, logo, useShellContext }),
    );

    // Timer hook for phone number verification countdown
    const { seconds, startTimer, isTimerRunning, stopTimer } = useTimer();

    const appState = {
        ...state,
        timer: { seconds, startTimer, isTimerRunning, stopTimer },
    };

    return (
        <AppContext.Provider value={{ state: appState, dispatch }}>{children}</AppContext.Provider>
    );
};

export { AppContext, AppProvider, computeInitialState, useAppContext, useAppDispatch };
